import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FaFire, FaArrowRight, FaInfoCircle } from 'react-icons/fa'

import { SectionHeader, Skeleton } from '../ui'
import HomeSection from './HomeSection'
import CountdownTimer from '../marketing/CountdownTimer'
import { productImage } from '../../utilies/imageUrl'
import { get_campaigns } from '../../store/reducers/campaignReducer'

// CampaignHomeSection — Hide-when-empty featured-campaign hero + preview cards.
// Always refetches with status=active so the slice can't leak listing-page state.

const fmtPrice = (n) => {
    const v = Number(n)
    if (!Number.isFinite(v) || v <= 0) return ''
    return `₼${v.toLocaleString('az-AZ')}`
}

// One small display-only product preview card. Click → product detail.
// Does NOT alter cart/checkout pricing.
const PreviewCard = ({ item, accent = '#7c3aed' }) => {
    const { t } = useTranslation()
    const p = item?.product
    if (!p?.slug) return null
    const dealPrice = Number(item.dealPrice) || 0
    const orig = Number(item.originalPriceSnapshot) || Number(p.price) || 0
    const showDeal = dealPrice > 0 && dealPrice < orig

    return (
        <Link
            to={`/product/details/${p.slug}`}
            className='group relative flex h-full flex-col overflow-hidden rounded-card border border-slate-200 bg-white shadow-card transition-all hover:shadow-card-hover hover:-translate-y-0.5'
        >
            {item.badgeText && (
                <span
                    className='absolute left-2 top-2 z-10 inline-flex items-center rounded-pill px-2 py-0.5 text-[10px] font-bold text-white shadow-card'
                    style={{ background: accent }}
                >
                    {item.badgeText}
                </span>
            )}
            <div className='aspect-square overflow-hidden bg-slate-100'>
                <img
                    src={productImage(p)}
                    alt={p.name}
                    loading='lazy'
                    decoding='async'
                    className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                />
            </div>
            <div className='flex flex-1 flex-col gap-1 p-3'>
                <h4 className='line-clamp-2 text-[13px] font-semibold text-slate-800 group-hover:text-brand-700 transition-colors'>
                    {p.name}
                </h4>
                <div className='mt-auto flex items-end gap-2'>
                    {showDeal ? (
                        <>
                            <span className='text-base font-black text-brand-700 tabular-nums leading-none'>
                                {fmtPrice(dealPrice)}
                            </span>
                            <span className='text-xs text-slate-400 line-through leading-none'>
                                {fmtPrice(orig)}
                            </span>
                        </>
                    ) : (
                        <span className='text-base font-black text-slate-900 tabular-nums leading-none'>
                            {fmtPrice(p.price)}
                        </span>
                    )}
                </div>
                {showDeal && (
                    <p className='mt-0.5 text-[10px] uppercase tracking-wider font-bold text-accent-700'>
                        {t('home.campaign.dealPrice')}
                    </p>
                )}
            </div>
        </Link>
    )
}

const CampaignHomeSection = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { campaigns, loader } = useSelector(s => s.campaign)

    // Always refetch with topItems so the home preview never serves stale data.
    useEffect(() => {
        dispatch(get_campaigns({
            page: 1,
            limit: 6,
            status: 'active',
            include: 'topItems',
            itemLimit: 4,
        }))
    }, [dispatch])

    const featured = useMemo(() => {
        const list = (campaigns || []).filter(c => c.status === 'active')
        if (list.length === 0) return null
        return list.find(c => c.isFeatured) || list[0]
    }, [campaigns])

    if (loader && !featured) {
        return (
            <HomeSection>
                <Skeleton radius='card' className='w-full aspect-[5/2]' />
            </HomeSection>
        )
    }
    if (!featured) return null

    const accent = featured.theme?.accent || '#7c3aed'
    const heroTitle    = featured.hero?.title    || featured.title
    const heroSubtitle = featured.hero?.subtitle || featured.description
    const previewItems = Array.isArray(featured.topItems) ? featured.topItems : []
    const hasDealDisplay = previewItems.some(it => {
        const dealPrice = Number(it?.dealPrice) || 0
        const orig = Number(it?.originalPriceSnapshot) || Number(it?.product?.price) || 0
        return dealPrice > 0 && dealPrice < orig
    })

    return (
        <HomeSection>
            <SectionHeader
                eyebrow={{ icon: <FaFire />, label: t('home.campaign.eyebrow'), tone: 'accent' }}
                title={t('home.campaign.title')}
                subtitle={t('home.campaign.subtitle')}
                action={{ to: '/kampaniyalar', label: t('home.campaign.cta') }}
            />

            {/* Hero card */}
            <Link
                to={`/kampaniya/${encodeURIComponent(featured.slug)}`}
                className='group relative block overflow-hidden rounded-card border border-slate-200 text-white shadow-card transition-all hover:shadow-card-hover'
                style={{ background: `linear-gradient(135deg, ${accent} 0%, #1e1b4b 100%)` }}
            >
                {featured.hero?.image && (
                    <img
                        src={featured.hero.image}
                        alt=''
                        loading='lazy'
                        className='absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-overlay transition-transform duration-500 group-hover:scale-[1.02]'
                    />
                )}
                <div className='relative z-10 grid grid-cols-12 gap-4 p-6 md:p-5'>
                    <div className='col-span-8 md-lg:col-span-12'>
                        <span className='inline-flex items-center gap-1.5 rounded-pill bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/15'>
                            <FaFire size={9} /> {t('home.campaign.activeBadge')}
                        </span>
                        <h3 className='mt-3 text-2xl sm:text-xl font-black leading-tight max-w-xl'>
                            {heroTitle}
                        </h3>
                        {heroSubtitle && (
                            <p className='mt-2 max-w-lg text-sm text-white/80'>{heroSubtitle}</p>
                        )}
                        <span className='mt-4 inline-flex items-center gap-2 rounded-card bg-white px-4 py-2 text-sm font-bold text-brand-700 group-hover:bg-slate-100 transition-colors'>
                            {t('home.campaign.viewCampaign')} <FaArrowRight size={11} />
                        </span>
                    </div>
                    <div className='col-span-4 md-lg:col-span-12 flex items-end md-lg:items-start justify-end md-lg:justify-start'>
                        {featured.status === 'active' && featured.endsAt && (
                            <CountdownTimer targetDate={featured.endsAt} variant='large' />
                        )}
                    </div>
                </div>
            </Link>

            {/* Product preview row */}
            {previewItems.length > 0 && (
                <div className='mt-4'>
                    <div className='grid grid-cols-4 md-lg:grid-cols-2 sm:grid-cols-1 gap-3'>
                        {previewItems.map((it, i) => (
                            <PreviewCard key={`${it?.product?._id || i}`} item={it} accent={accent} />
                        ))}
                    </div>
                    {hasDealDisplay && (
                        <p className='mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-500'>
                            <FaInfoCircle size={10} className='text-slate-400' />
                            {t('home.campaign.dealNote')}
                        </p>
                    )}
                </div>
            )}
        </HomeSection>
    )
}

export default CampaignHomeSection
